import React, { useState } from 'react';

import { toast } from 'sonner';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';

import { useCognito } from '@sandtech-hub-monorepo/ui-auth-kit';
import { LeftCard, RightCard } from '@sandtech-hub-monorepo/ui-kit';
import { ForgotPasswordDocument } from '@sandtech-hub-monorepo/ehub-client';

import { ResetPasswordForm } from './components/ResetPasswordForm';
import {
  forgotPasswordCTAQuestion,
  forgotPasswordCTAText,
  forgotPasswordSubtitle,
  forgotPasswordTitle,
} from '../../../../stringsManager';
import { ConfirmResetPasswordForm } from './components/ConfirmResetPasswordForm';

export interface ResetPasswordProps {
  loginRoute: string;
  forgotPasswordCover: string;
  isStaff?: boolean;
}

export const ResetPassword = ({ isStaff, ...props }: ResetPasswordProps) => {
  const navigate = useNavigate();

  const [isResetPwdCodeSent, setIsResetPwdCodeSent] = useState(false);
  const [email, setEmail] = useState('');

  const { loading: confirmLoading, confirmResetPassword } = useCognito();
  const [params] = useSearchParams();
  const [forgotPassword, { loading: forgotPasswordLoading }] = useMutation(
    ForgotPasswordDocument
  );

  const handleResetPassword = (values: { email: string }) => {
    forgotPassword({
      variables: { input: { email: values.email, isStaff: isStaff ?? false } },
      onCompleted: () => {
        setIsResetPwdCodeSent(true);
        setEmail(values.email);
      },
    });
  };

  const handleConfirmResetPassword = async (values: {
    email: string;
    newPassword: string;
    confirmNewPassword: string;
    confirmationCode: string;
  }) => {
    confirmResetPassword({
      username: values.email,
      newPassword: values.newPassword,
      confirmationCode: values.confirmationCode,
    }).then(() => {
      toast.success('You have successfully reset your password!');
    });

    navigate({ pathname: props.loginRoute, search: params.toString() });
  };

  return (
    <>
      <LeftCard backgroundImage={props.forgotPasswordCover} />
      <RightCard>
        <div className="flex flex-col space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            {forgotPasswordTitle}
          </h1>
          {!isResetPwdCodeSent && (
            <p className="text-sm text-muted-foreground">
              {forgotPasswordSubtitle}
            </p>
          )}
        </div>
        {isResetPwdCodeSent ? (
          <ConfirmResetPasswordForm
            email={email}
            onSubmit={handleConfirmResetPassword}
            loading={confirmLoading}
          />
        ) : (
          <ResetPasswordForm
            onSubmit={handleResetPassword}
            loading={forgotPasswordLoading}
          />
        )}

        <div className="flex gap-1 justify-center leading-tight">
          <p className="text-sm text-muted-foreground">
            {forgotPasswordCTAQuestion}
          </p>
          <Link
            to={`${props.loginRoute}${
              params.size > 0 ? '?' + params.toString() : ''
            }`}
            className="text-sm text-primary"
          >
            {forgotPasswordCTAText}
          </Link>
        </div>
      </RightCard>
    </>
  );
};
